import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Stack,
  IconButton,
  Button,
} from "@mui/material";
import {
  WarningAmber as WarningIcon,
  DeleteForever as DeleteIcon,
  Close as CloseIcon,
  Info as InfoIcon,
} from "@mui/icons-material";

/**
 * Reusable bulk/single delete confirmation dialog (destructive action layout).
 */
const DeletionDialog = ({
  open,
  onClose,
  title,
  count,
  itemSingular = "item",
  itemPlural,
  previewTitles = [],
  previewLabel = "Including",
  untitledLabel = "(Untitled)",
  warning = null,
  deleting,
  onConfirm,
  cancelLabel = "Cancel",
  confirmLabel = "Yes, delete",
  deletingLabel = "Deleting…",
}) => {
  const plural =
    itemPlural ?? (itemSingular.endsWith("s") ? itemSingular : `${itemSingular}s`);
  const itemWord = count === 1 ? itemSingular : plural;

  const emptyPreviewMessage = `${count} ${itemWord} will be removed.`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: 400,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          overflow: "visible",
        },
      }}
    >
      <Box
        sx={{
          p: 2.5,
          bgcolor: "error.lighter",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTopLeftRadius: "inherit",
          borderTopRightRadius: "inherit",
        }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              p: 1,
              bgcolor: "error.main",
              borderRadius: "50%",
              display: "flex",
              color: "white",
            }}
          >
            <WarningIcon fontSize='small' />
          </Box>
          <Typography variant='h6' fontWeight={700} color='error.main'>
            {title}
          </Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          disabled={deleting}
          sx={{ color: "error.main" }}
          size='small'
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, pt: 2 }}>
        <Stack spacing={2}>
          <Typography variant='body1' color='text.primary'>
            Are you sure you want to permanently delete <strong>{count}</strong>{" "}
            {itemWord}? This cannot be undone.
          </Typography>

          {previewTitles.length > 0 ? (
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction='row' spacing={1} alignItems='flex-start' sx={{ mb: 1 }}>
                <InfoIcon color='action' sx={{ mt: 0.25 }} />
                <Typography variant='caption' color='text.secondary' fontWeight={600}>
                  {previewLabel}
                </Typography>
              </Stack>
              <Stack spacing={0.75} sx={{ pl: 0.5 }}>
                {previewTitles.map((t, i) => (
                  <Typography
                    key={`${t}-${i}`}
                    variant='body2'
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {t || untitledLabel}
                  </Typography>
                ))}
                {count > previewTitles.length ? (
                  <Typography variant='body2' color='text.secondary'>
                    … and {count - previewTitles.length} more
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <InfoIcon color='action' />
              <Typography variant='body2' color='text.secondary'>
                {emptyPreviewMessage}
              </Typography>
            </Box>
          )}

          {warning}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
          <Button
            onClick={onClose}
            disabled={deleting}
            fullWidth
            variant='outlined'
            sx={{ borderRadius: 2, py: 1.2 }}
          >
            {cancelLabel}
          </Button>
          <Button
            color='error'
            variant='contained'
            onClick={onConfirm}
            disabled={deleting}
            startIcon={deleting ? null : <DeleteIcon />}
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              bgcolor: "error.main",
              "&:hover": { bgcolor: "error.dark" },
            }}
          >
            {deleting ? deletingLabel : confirmLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DeletionDialog;
